import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Exercise as ExercisePagination } from '@tekman/onmat/component/activities';
import {
  Activity,
  CheckboxItemNode,
  Feature,
  GroupEvaluationStatus,
  StudentsGroup,
  StudentSubcompetences,
  SubCompetencesAssessmentStudents,
  UpdatedActivityFeature,
} from '@tekman/onmat/models';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import * as activitiesActions from '../+state/activities.actions';
import * as activitiesSelectors from '../+state/activities.selectors';
import { ActivitiesTeacherService } from '../service/activities-teacher.service';

@Injectable({
  providedIn: 'root',
})
export class ActivitiesFacade {
  activity$ = this._store.pipe(
    select(activitiesSelectors.getActivityLoaded),
    filter(activity => !!activity),
    distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current))
  );

  steps$ = this._store.pipe(select(activitiesSelectors.getStepsLoaded));
  status$ = this._store.pipe(select(activitiesSelectors.getStatusActivityLoaded));
  tags$ = this._store.pipe(select(activitiesSelectors.getTagsLoaded));
  features$ = this._store.pipe(select(activitiesSelectors.getFeaturesLoaded));
  rubrics$ = this._store.pipe(
    select(activitiesSelectors.getActivityRubricsLoaded),
    filter(rubric => !!rubric)
  );
  materials$ = this._store.pipe(select(activitiesSelectors.getMaterialsActivityLoaded));

  studentsGroupStatus$ = this._store.pipe(
    select(activitiesSelectors.getActivityStudentsStatus),
    filter(status => !!status)
  );
  teachingInfo$ = this._store.pipe(select(activitiesSelectors.getTeachingInfoActivityLoaded));
  averageGrades$ = this._store.pipe(select(activitiesSelectors.getAverageGrades));
  studentAverages$ = this._store.pipe(select(activitiesSelectors.getStudentAverages));
  studentAnswers$ = this._store.pipe(
    select(activitiesSelectors.getStudentAnswersActivityLoaded),
    filter(studentAnswers => !!studentAnswers)
  );
  rubricsMultiChoice$ = this._store.pipe(select(activitiesSelectors.getRubricsMultiChoice));

  isLoadedActivity$ = this._store.pipe(
    select(activitiesSelectors.getLoading),
    filter(loading => !!loading)
  );
  isLoadingCursor$ = this.activitiesService.isLoadingCursor$;
  isLoadingAnswersStudent$ = this.activitiesService.isLoadingAnswersStudent$;
  exercises$ = this._store.pipe(
    select(activitiesSelectors.getExercises),
    filter(exercises => !!exercises)
  );
  constructor(private readonly _store: Store, private readonly activitiesService: ActivitiesTeacherService) {}

  routerActivityId(id: string): void {
    this._store.dispatch(activitiesActions.clearActivity());
    this._store.dispatch(activitiesActions.teacherRouterSuccess({ id }));
  }

  saveEvaluationPages(activity: Activity, exercise: ExercisePagination): void {
    this._store.dispatch(activitiesActions.saveEvaluationPages({ activity, exercise }));
  }

  saveFeature(activity: Activity, selectedGroupId: string, feature: UpdatedActivityFeature, features: Feature[], studentId?: string): void {
    this._store.dispatch(activitiesActions.saveFeature({ activity, selectedGroupId, feature, features, studentId }));
  }

  loadSubcompetencesAssessmentStudents(activity: string, teams: CheckboxItemNode[]): void {
    this._store.dispatch(activitiesActions.loadSubcompetencesAssessmentStudents({ activity, teams }));
  }

  loadTeacherGuide(activity: Activity, exerciseId: string): void {
    this._store.dispatch(activitiesActions.loadTeacherGuide({ activity, exerciseId }));
  }

  loadTeacherSolutionary(activity: Activity, exerciseId: string): void {
    this._store.dispatch(activitiesActions.loadTeacherSolutionary({ activity, exerciseId }));
  }

  loadMaterials(activity: Activity): void {
    this._store.dispatch(activitiesActions.loadActivityMaterials({ activity }));
  }

  loadActivityStudentAnswersEvaluation(activity: Activity, studentId: string, status: number) {
    this._store.dispatch(activitiesActions.loadActivityStudentAnswers({ activity, studentId, status }));
  }

  loadStudentMultianswers(activityId: string, studentId: string): void {
    this._store.dispatch(activitiesActions.loadStudentMultianswersSuccess({ activityId, studentId }));
  }

  repeatActivity(activity: Activity, selectedStudent: string): void {
    this._store.dispatch(activitiesActions.repeatActivity({ activity, selectedStudent }));
  }

  autoSaveEvaluationGrades(
    activity: Activity,
    subcompetencesAssessmentStudent: StudentSubcompetences,
    teams: CheckboxItemNode[],
    studentsGroup: GroupEvaluationStatus,
    selectedStudent: string
  ): void {
    this._store.dispatch(
      activitiesActions.autoSaveEvaluationGrades({
        activity,
        subcompetencesAssessmentStudent,
        teams,
        studentsGroup,
        selectedStudent,
      })
    );
  }

  applySubcompetenceAssessmentStudents(
    activity: Activity,
    subcompetencesAssessmentStudents: SubCompetencesAssessmentStudents,
    teams: CheckboxItemNode[],
    studentsGroup: StudentsGroup[]
  ): void {
    this._store.dispatch(
      activitiesActions.applySubcompetencesAssessmentStudents({ activity, subcompetencesAssessmentStudents, teams, studentsGroup })
    );
  }

  publishMarks(
    activity: Activity,
    subcompetencesAssessmentStudent: StudentSubcompetences,
    groupEvaluation: GroupEvaluationStatus,
    studentId: string
  ): void {
    this._store.dispatch(activitiesActions.publishMarks({ activity, subcompetencesAssessmentStudent, groupEvaluation, studentId }));
  }

  unPublishMarks(
    activity: Activity,
    subcompetencesAssessmentStudent: StudentSubcompetences,
    groupEvaluation: GroupEvaluationStatus,
    studentId: string
  ): void {
    this._store.dispatch(activitiesActions.unPublishMarks({ activity, subcompetencesAssessmentStudent, groupEvaluation, studentId }));
  }
}
